// Central registry of all PostHog custom event names.
// Import from here — never hardcode event name strings in components.

export const AnalyticsEvents = {
  PAGEVIEW_EXTENDED: "pageview_extended",
  SCROLL_DEPTH_REACHED: "scroll_depth_reached",
  LEAD_INFO_CAPTURED: "lead_info_captured",
  FORM_VALIDATION_ERROR: "form_validation_error",
  TAB_VISIBILITY_LOST: "tab_visibility_lost",
  TAB_VISIBILITY_RESTORED: "tab_visibility_restored",
  BOOKING_FUNNEL_STEP: "booking_funnel_step",
  BOOKING_CHECKOUT_INITIATED: "booking_checkout_initiated",
  BOOKING_CHECKOUT_REQUESTED: "booking_checkout_requested",
  BOOKING_CHECKOUT_CREATED: "booking_checkout_created",
  BOOKING_CHECKOUT_REDIRECTED: "booking_checkout_redirected",
  BOOKING_CHECKOUT_FAILED: "booking_checkout_failed",
  BOOKING_COMPLETED: "booking_completed",
  BOOKING_FAILED: "booking_failed",
  BOOKING_STRIPE_RETURNED: "booking_stripe_returned",
  BOOKING_STRIPE_RETURN_FAILED: "booking_stripe_return_failed",
  BOOKING_PAYMENT_VERIFY_STARTED: "booking_payment_verify_started",
  BOOKING_PAYMENT_VERIFY_RETRY: "booking_payment_verify_retry",
  BOOKING_PAYMENT_VERIFY_SUCCEEDED: "booking_payment_verify_succeeded",
  BOOKING_PAYMENT_VERIFY_FAILED: "booking_payment_verify_failed",
  BOOKING_PAYMENT_VERIFY_TIMEOUT: "booking_payment_verify_timeout",
  CART_RESUME_CLICKED: "cart_resume_clicked",
  BOOK_RESUME_LOADED: "book_resume_loaded",
  BOOK_RESUME_NOT_FOUND: "book_resume_not_found",
  BOOK_RESUME_LOAD_FAILED: "book_resume_load_failed",
  DRAFT_DISCARDED: "draft_discarded",
  DRAFT_DISCARD_FAILED: "draft_discard_failed",
  ADDON_CHECKOUT_STARTED: "addon_checkout_started",
  ADDON_CHECKOUT_REDIRECTED: "addon_checkout_redirected",
  ADDON_CHECKOUT_FAILED: "addon_checkout_failed",
  ADDON_PAYMENT_VERIFY_STARTED: "addon_payment_verify_started",
  ADDON_PAYMENT_VERIFIED: "addon_payment_verified",
  ADDON_PAYMENT_TIMEOUT: "addon_payment_timeout",
  ADDON_PAYMENT_RECOVERED: "addon_payment_recovered",
  ADDON_REJECTED: "addon_rejected",
  ADDON_REJECT_FAILED: "addon_reject_failed",
  FREQUENCY_TOGGLED: "frequency_toggled",
  BACK_BUTTON_ON_CHECKOUT: "back_button_on_checkout",
  FIELD_TYPING_TIME: "field_typing_time",
  DATE_CLICKED: "date_clicked",
  TIME_SLOT_CLICKED: "time_slot_clicked",
  SERVICE_CARD_HOVERED: "service_card_hovered",
  SERVICE_CARD_CLICKED: "service_card_clicked",
  FREQUENCY_CARD_HOVERED: "frequency_card_hovered",
  SERVICE_NAV_CLICKED: "service_nav_clicked",
  SERVICES_MENU_OPENED: "services_menu_opened",
  SERVICE_PAGE_VIEWED: "service_page_viewed",
  AVAILABILITY_FETCH_STARTED: "availability_fetch_started",
  AVAILABILITY_FETCH_COMPLETED: "availability_fetch_completed",
  AVAILABILITY_FETCH_FAILED: "availability_fetch_failed",
  OTP_REQUESTED: "otp_requested",
  OTP_DELIVERED: "otp_delivered",
  OTP_SEND_FAILED: "otp_send_failed",
  OTP_VERIFIED: "otp_verified",
  OTP_FAILED: "otp_failed",
  OTP_RESEND_REQUESTED: "otp_resend_requested",
  OTP_RESEND_DELIVERED: "otp_resend_delivered",
  OTP_RESEND_FAILED: "otp_resend_failed",
  USER_SIGNED_UP: "user_signed_up",
  USER_LOGGED_IN: "user_logged_in",
  WEB_VITALS: "web_vitals",

  // Package upsell experiment ("package-upsell-frequency") + purchase funnel.
  // PACKAGE_UPSELL_SHOWN/ACCEPTED/DISMISSED measure the prompt itself;
  // PACKAGE_PURCHASE_COMPLETED is the experiment's primary conversion metric.
  PACKAGE_UPSELL_SHOWN: "package_upsell_shown",
  PACKAGE_UPSELL_ACCEPTED: "package_upsell_accepted",
  PACKAGE_UPSELL_DISMISSED: "package_upsell_dismissed",

  // Hidden/exclusive package upsell (shown when a promo code unlocks a
  // hidden package definition). Mirrors PACKAGE_UPSELL_* above but tracked
  // separately since it's a distinct entry point (promo unlock, not the
  // "package-upsell-frequency" experiment).
  PACKAGE_HIDDEN_UPSELL_SHOWN: "package_hidden_upsell_shown",
  PACKAGE_HIDDEN_UPSELL_ACCEPTED: "package_hidden_upsell_accepted",
  PACKAGE_HIDDEN_UPSELL_DISMISSED: "package_hidden_upsell_dismissed",

  // Referral programme upsell dialog on the public pages. Shown once per visitor,
  // then not again for 7 days (see constants/referralUpsell.ts). ACCEPTED covers
  // both branches of the CTA — a guest clicking through to signup, and a member
  // copying or sharing their code.
  REFERRAL_UPSELL_SHOWN: "referral_upsell_shown",
  REFERRAL_UPSELL_ACCEPTED: "referral_upsell_accepted",
  REFERRAL_UPSELL_DISMISSED: "referral_upsell_dismissed",

  // A referral link was followed, but the session resolved to an account that already
  // existed. Referrals are for new homeowners only, so the code is discarded — this
  // measures how much referral traffic lands on existing customers, which is otherwise
  // invisible (no referral row is ever written, so there is nothing to count).
  REFERRAL_CODE_DISCARDED_EXISTING_ACCOUNT: "referral_code_discarded_existing_account",

  PACKAGE_CHECKOUT_STARTED: "package_checkout_started",
  PACKAGE_CHECKOUT_FAILED: "package_checkout_failed",
  PACKAGE_PURCHASE_COMPLETED: "package_purchase_completed",
  PACKAGE_PURCHASE_FAILED: "package_purchase_failed",

  // Office cleaning enquiry form — A/B test between standard and carousel variants
  CLICK_QUOTE_BUTTON: "click-quote-button-event",
  OFFICE_FORM_VARIANT_SHOWN: "office_form_variant_shown",
  OFFICE_FORM_STEP_COMPLETED: "office_form_step_completed",
  OFFICE_FORM_SUBMITTED: "office_form_submitted",
  OFFICE_FORM_SUBMISSION_SUCCESS: "office_form_submission_success",
  OFFICE_FORM_SUBMISSION_ERROR: "office_form_submission_error",
} as const;

export type AnalyticsEventName =
  (typeof AnalyticsEvents)[keyof typeof AnalyticsEvents];

// Named funnel steps for BOOKING_FUNNEL_STEP events.
// Keeps PostHog funnels consistent across cleaning & aircon forms.
export const BookingFunnelStep = {
  FORM_OPENED: "form_opened",
  ADDRESS_ENTERED: "address_entered",
  DATE_SELECTED: "date_selected",
  TIME_SELECTED: "time_selected",
  FREQUENCY_SELECTED: "frequency_selected",
  PHONE_GATE_TRIGGERED: "phone_gate_triggered",
  PHONE_VERIFIED: "phone_verified",
  CHECKOUT_SUBMITTED: "checkout_submitted",
  STRIPE_REDIRECTED: "stripe_redirected",
  PAYMENT_SUCCESS: "payment_success",
  PAYMENT_FAILED: "payment_failed",
} as const;

export type BookingFunnelStepName =
  (typeof BookingFunnelStep)[keyof typeof BookingFunnelStep];
